import { Realization, RealizationSubscribed } from "types/realization";

function isRealization(data: any): data is Realization {
        return data && typeof data === 'object' && '_id' in data && 'owner' in data && 'indicator' in data;
}

function isRealizationSubscribed(data: any): data is RealizationSubscribed {
    return data && typeof data === 'object' && '_id' in data && ('concorrentes_info' in data || 'concorrentes' in data) && 'realization' in data;
}

function clearRegistrationUser(player: string): string{
    return player.replace(/^F?(0+)?/gi,"").toString()
}

/**
 * Extrai o GUID de um ID codificado em base64url do SharePoint.
 * O formato interno é: [1 byte tipo][1 byte separador][16 bytes GUID][bytes extras]
 *
 * @param encodedId O ID codificado (ex: IQBnSoRDvf_jRqpi5vErfKZpAQdrgw3kWsDYN9ml35nJ9L8)
 * @returns O GUID no formato string ou null se não for possível extrair
 */
function extractGuidFromSharePointId(encodedId: string): string | null {
  try {
    // Converte base64url para base64 padrão
    let base64 = encodedId.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Precisamos de pelo menos 2 bytes de header + 16 bytes do GUID
    if (bytes.length < 18) return null;

    // GUID em formato mixed-endian (padrão Microsoft):
    // Data1 (bytes 2-5, little-endian uint32)
    // Data2 (bytes 6-7, little-endian uint16)
    // Data3 (bytes 8-9, little-endian uint16)
    // Data4 (bytes 10-17, big-endian 8 bytes)
    const data1 = ((bytes[5] << 24) | (bytes[4] << 16) | (bytes[3] << 8) | bytes[2]) >>> 0;
    const data2 = ((bytes[7] << 8) | bytes[6]) >>> 0;
    const data3 = ((bytes[9] << 8) | bytes[8]) >>> 0;

    const hex = (n: number, len: number) => n.toString(16).padStart(len, '0');
    const data4Hex = Array.from(bytes.slice(10, 18))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    return `${hex(data1, 8)}-${hex(data2, 4)}-${hex(data3, 4)}-${data4Hex.slice(0, 4)}-${data4Hex.slice(4)}`;
  } catch {
    return null;
  }
}

/**
 * Converte uma URL de compartilhamento do SharePoint/OneDrive (formato :v:)
 * para uma URL de embed que funciona em iframes.
 *
 * Formato de entrada: https://{tenant}-my.sharepoint.com/:v:/g/personal/{user}/{encodedId}
 * Formato de saída:   https://{tenant}-my.sharepoint.com/personal/{user}/_layouts/15/embed.aspx?UniqueId={guid}
 *
 * Também aceita URLs de sites: https://{tenant}.sharepoint.com/:v:/s/{site}/{encodedId}
 *
 * Se a URL já for um embed.aspx, retorna como está.
 * Se não for possível converter, retorna null.
 */
function convertSharePointToEmbed(shareUrl: string): string | null {
  try {
    const url = new URL(shareUrl);

    // Já é uma URL de embed
    if (url.pathname.toLowerCase().includes('embed.aspx')) {
      return shareUrl;
    }

    const appendSharingParams = (embedUrl: string): string => {
      const result = new URL(embedUrl);
      const nav = url.searchParams.get('nav');
      const e = url.searchParams.get('e');
      if (nav) result.searchParams.set('nav', nav);
      if (e) result.searchParams.set('e', e);
      return result.toString();
    };

    // Match links pessoais: /:v:/g/personal/{user}/{encodedId}
    const personalMatch = url.pathname.match(
      /^\/:v:\/[grst]\/personal\/([^/]+)\/(.+)$/i
    );

    if (personalMatch) {
      const [, user, encodedId] = personalMatch;
      const guid = extractGuidFromSharePointId(encodedId);
      if (guid) {
        return appendSharingParams(`${url.origin}/personal/${user}/_layouts/15/embed.aspx?UniqueId=${guid}`);
      }
    }

    // Match links de sites: /:v:/s/{site}/{encodedId}
    const siteMatch = url.pathname.match(
      /^\/:v:\/[grst]\/sites?\/([^/]+)\/(.+)$/i
    );

    if (siteMatch) {
      const [, site, encodedId] = siteMatch;
      const guid = extractGuidFromSharePointId(encodedId);
      if (guid) {
        return appendSharingParams(`${url.origin}/sites/${site}/_layouts/15/embed.aspx?UniqueId=${guid}`);
      }
    }

    // Match links de equipes: /:v:/t/{team}/{encodedId}
    const teamMatch = url.pathname.match(
      /^\/:v:\/t\/([^/]+)\/(.+)$/i
    );

    if (teamMatch) {
      const [, team, encodedId] = teamMatch;
      const guid = extractGuidFromSharePointId(encodedId);
      if (guid) {
        return appendSharingParams(`${url.origin}/teams/${team}/_layouts/15/embed.aspx?UniqueId=${guid}`);
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Extrai a URL da thumbnail de um vídeo do SharePoint.
 *
 * O SharePoint expõe thumbnails via `getpreview.ashx?UniqueId=<guid>` para
 * URLs no formato embed.aspx (código de incorporação).
 * Também aceita links de compartilhamento (:v:), convertendo-os primeiro.
 *
 * @param videoUrl URL do vídeo SharePoint (embed, link direto ou sharing link)
 * @returns URL da thumbnail ou undefined quando não for possível extrair
 */
function getSharePointVideoThumbnail(videoUrl?: string): string | undefined {
  if (!videoUrl) return undefined;

  try {
    // Se for um link de compartilhamento (:v:), converte para embed primeiro
    const embedUrl = convertSharePointToEmbed(videoUrl) || videoUrl;
    const url = new URL(embedUrl);

    // Formato embed: .../sites/<site>/_layouts/15/embed.aspx?UniqueId=<guid>&...
    if (url.pathname.toLowerCase().includes('embed.aspx')) {
      const uniqueId = url.searchParams.get('UniqueId');
      if (uniqueId) {
        const previewPath = url.pathname.replace(/embed\.aspx$/i, 'getpreview.ashx');
        return `${url.origin}${previewPath}?UniqueId=${encodeURIComponent(uniqueId)}&Resolution=6`;
      }
    }

    return undefined;
  } catch {
    return undefined;
  }
}

export {
    isRealization,
    isRealizationSubscribed,
    clearRegistrationUser,
    getSharePointVideoThumbnail,
    convertSharePointToEmbed
}